import { useState, useMemo, useEffect } from 'react';
import { Search as SearchIcon } from 'lucide-react';
import { useAccounts } from '@/hooks/account/useAccounts';
import { TransactionBuilderAccountCard } from '../../components/transaction-builder/TransactionBuilderAccountCard';
import {
  TransactionBuilderPageType,
  useTransactionBuilderStore,
} from '../../components/transaction-builder/useTransactionBuilderStore';
import { TransactionBuilderFooter } from '../../components/transaction-builder/TransactionBuilderFooter';
import { TransactionBuilderHeader } from '../../components/transaction-builder/TransactionBuilderHeader';
import { Account } from '@mls-wallet/shared-types';

/**
 * Account selection page for the transaction builder
 * Displays a list of accounts that the user can select to send a transaction from
 * Includes a search bar to filter accounts by name
 */
export function TransactionBuilderAccountSelectionPage(): React.ReactElement {
  const { accounts } = useAccounts();
  const setAccount = useTransactionBuilderStore((state) => state.setAccount);
  const setNetwork = useTransactionBuilderStore((state) => state.setNetwork);
  const navigateForwardToPage = useTransactionBuilderStore(
    (state) => state.navigateForwardToPage
  );

  const [searchQuery, setSearchQuery] = useState('');

  // Reset account when the page loads
  // This is to ensure that the header display is correct
  useEffect(() => {
    setAccount(undefined);
  }, [setAccount]);

  /**
   * Filter accounts based on search query matching account name
   */
  const filteredAccounts = useMemo(() => {
    // Case: no search query, return all accounts
    if (!searchQuery.trim()) {
      return accounts;
    }

    const queryLowerCase = searchQuery.toLowerCase();
    return accounts.filter((account) =>
      account.name?.toLowerCase().includes(queryLowerCase)
    );
  }, [accounts, searchQuery]);

  const handleAccountSelect = (account: Account) => {
    setAccount(account);

    // Skip network selection, but let the cart start the child edit session.
    if (account.networks.length === 1 && account.networks[0]) {
      setNetwork(account.networks[0]);
      navigateForwardToPage({
        page: TransactionBuilderPageType.CART_PAGE,
      });
    } else {
      navigateForwardToPage({
        page: TransactionBuilderPageType.NETWORK_SELECTION_PAGE,
      });
    }
  };

  return (
    <>
      <TransactionBuilderHeader
        title="Which account do you want to send a transaction from?"
        showBackButton={false}
      />
      <div className="flex flex-col w-full max-w-4xl mx-auto px-4 py-6 gap-[30px]">
        {/* Search bar */}
        <div className="bg-neutral-50 dark:bg-neutral-900 border border-white dark:border-zinc-800 rounded-full p-[5px] shadow-[0px_4px_6px_-3px_rgba(0,0,0,0.1)] my-[10px]">
          <div className="bg-white dark:bg-zinc-950 border border-zinc-100 dark:border-zinc-800 rounded-full px-[22px] py-[10px] flex items-center gap-[10px]">
            <SearchIcon className="w-4 h-4 text-zinc-500 dark:text-zinc-400 shrink-0" />
            <input
              type="text"
              placeholder="Search accounts"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-[16px] text-foreground placeholder:text-zinc-500 dark:placeholder:text-zinc-400 outline-none border-none"
            />
          </div>
        </div>

        {/* Account list */}
        <div className="flex flex-col">
          {filteredAccounts.map((account, idx) => (
            <div
              key={account.id}
              style={
                idx !== filteredAccounts.length - 1
                  ? { marginBottom: '43px' }
                  : undefined
              }
            >
              <TransactionBuilderAccountCard
                account={account}
                onClick={() => handleAccountSelect(account)}
              />
            </div>
          ))}
          {filteredAccounts.length === 0 && (
            <div className="text-center text-zinc-500 dark:text-zinc-400 py-8">
              No accounts found matching "{searchQuery}"
            </div>
          )}
        </div>
      </div>
      <TransactionBuilderFooter showContinueButton={false} />
    </>
  );
}
